/** Cookie credentials never enter this module's storage or event payloads. */
export type SessionMeta = {
  epoch: string; status: 'active' | 'out'; sessionId?: string; expiresAt?: number;
  pending?: {requestId: string; startedAt: number}; retryAt?: number;
};
type Transport = (path: string, body: unknown) => Promise<any>;
const terminal = new Set(['SESSION_REVOKED', 'SESSION_REPLACED', 'SESSION_EXPIRED', 'INVALID_REFRESH', 'REFRESH_REUSE_DETECTED', 'REFRESH_RETRY_EXPIRED', 'LOCAL_SIGNED_OUT']);
export const authCode = (error: any): string => error?.body?.data?.code || error?.code || '';
export const sessionEnded = (error: unknown) => terminal.has(authCode(error));
export const accessExpired = (error: any) => error?.status === 401 && ['ACCESS_EXPIRED', 'INVALID_TOKEN', 'token_required'].includes(authCode(error));
function failure(code: string, message = '登录已失效，请重新登录') {
  return Object.assign(new Error(message), {code, status: 401, body: {data: {code}}});
}

export class SessionCoordinator {
  private timer?: ReturnType<typeof setTimeout>;
  private flight?: Promise<void>;
  private readonly lead = 60 + Math.random() * 30;
  private readonly key: string;
  private readonly lockName: string;
  constructor(private base: string, private transport: Transport) {
    const scope = new URL(base, window.location.origin).href.replace(/\/$/, '');
    localStorage.removeItem('locs-user-credentials');
    localStorage.removeItem('locs_user_credentials');
    this.key = `locs.session.v1:${scope}`;
    this.lockName = `locs.auth:${scope}`;
    window.addEventListener('storage', e => { if (e.key === this.key) { this.notify(); this.schedule(); } });
    window.addEventListener('online', () => this.resume());
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.resume(); else this.stopTimer(); });
    this.schedule();
  }
  read(): SessionMeta | null {
    const value = localStorage.getItem(this.key);
    if (!value) return null;
    try {
      const meta = JSON.parse(value);
      if (typeof meta.epoch !== 'string' || !['active', 'out'].includes(meta.status)) throw Error();
      return meta;
    } catch { throw failure('SESSION_STORAGE_INVALID', '登录状态读取失败，请重新登录'); }
  }
  private write(meta: SessionMeta, notify = true) { localStorage.setItem(this.key, JSON.stringify(meta)); if (notify) this.notify(); this.schedule(); }
  private notify() { window.dispatchEvent(new CustomEvent('locs-session-change', {detail: {base: this.base}})); }
  private stopTimer() { if (this.timer) clearTimeout(this.timer); this.timer = undefined; }
  private schedule() {
    this.stopTimer();
    const meta = this.read();
    if (!meta || meta.status !== 'active' || (!meta.expiresAt && !meta.pending) || document.hidden) return;
    const due = Math.max(meta.expiresAt ? (meta.expiresAt - this.lead) * 1000 : Date.now(), meta.retryAt || 0);
    this.timer = setTimeout(() => this.resume(), Math.max(1000, due - Date.now()));
  }
  private resume() { void this.ensure().catch(() => { /* transport reports a redacted event; preserve state */ }); }
  private lock<T>(fn: () => Promise<T>): Promise<T> {
    if (!navigator.locks) return Promise.reject(failure('WEB_LOCKS_REQUIRED', '请使用支持安全会话恢复的新版浏览器（HTTPS）'));
    return navigator.locks.request(this.lockName, {signal: AbortSignal.timeout(15000)}, fn).catch(error => {
      if (error?.name === 'TimeoutError') throw failure('SESSION_LOCK_TIMEOUT', '另一个页面正在恢复登录，请稍后重试');
      throw error;
    });
  }
  private assertEpoch(epoch: string | undefined) {
    const current = this.read();
    if (current?.status === 'out' || current?.epoch !== epoch) throw failure('LOCAL_SIGNED_OUT');
  }
  async login<T>(operation: () => Promise<T>): Promise<T> {
    const observedEpoch = this.read()?.epoch;
    return this.lock(async () => {
      if (this.read()?.epoch !== observedEpoch) throw failure('LOCAL_SIGNED_OUT');
      const epoch = crypto.randomUUID();
      this.write({epoch, status: 'active'});
      try {
        const result = await operation();
        if (this.read()?.epoch !== epoch || this.read()?.status === 'out') {
          await this.transport('/auth/refresh/revoke', {});
          throw failure('LOCAL_SIGNED_OUT');
        }
        this.commit(result, epoch);
        return result;
      } catch (error) {
        if (this.read()?.epoch === epoch) this.write({epoch, status: 'out'}, false);
        throw error;
      }
    });
  }
  private commit(result: any, epoch: string) {
    const item = result?.data?.[0];
    if (result?.status !== 'Success' || typeof item?.sessionId !== 'string' || !Number.isFinite(item?.expiresAt)) {
      throw failure('INVALID_SESSION_RESPONSE', '无法读取登录状态，请重试');
    }
    const prior = this.read();
    if (prior?.sessionId && prior.sessionId !== item.sessionId) throw failure('SESSION_REPLACED');
    this.write({epoch, status: 'active', sessionId: item.sessionId, expiresAt: item.expiresAt});
  }
  ensure(force = false, observedExpiry?: number): Promise<void> {
    if (this.flight) return this.flight;
    this.flight = this.lock(async () => {
      const meta = this.read();
      if (meta?.status === 'out') throw failure('LOCAL_SIGNED_OUT');
      if (meta?.retryAt && meta.retryAt > Date.now()) throw failure('SESSION_RETRY_LATER', '连接暂不可用，请稍后重试');
      if (!force && !meta?.pending && meta?.expiresAt && meta.expiresAt * 1000 > Date.now() + this.lead * 1000) return;
      if (force && meta?.expiresAt !== observedExpiry && meta?.expiresAt && meta.expiresAt * 1000 > Date.now() && !meta.pending) return;
      const epoch = meta?.epoch || crypto.randomUUID();
      if (meta?.pending && Date.now() - meta.pending.startedAt >= 30000) {
        this.write({epoch, status: 'out'}); throw failure('REFRESH_RETRY_EXPIRED');
      }
      const pending = meta?.pending || {requestId: crypto.randomUUID(), startedAt: Date.now()};
      this.write({...meta, epoch, status: 'active', pending});
      try {
        const result = await this.transport('/auth/refresh', {requestId: pending.requestId});
        this.assertEpoch(epoch);
        this.commit(result, epoch);
      } catch (error: any) {
        if (this.read()?.epoch === epoch && this.read()?.status !== 'out') {
          if (sessionEnded(error)) this.write({epoch, status: 'out'});
          else {
            // A received 429 is pre-execution; other failures may have rotated credentials.
            this.write({...this.read()!, pending: error?.status === 429 && !meta?.pending ? undefined : pending,
              retryAt: Date.now() + Math.max(1000, error?.retryAfterMs || 2000)});
          }
        }
        console.info(JSON.stringify({event:'session.refresh',outcome:authCode(error)||'transport_failure',ts:new Date().toISOString()}));
        throw error;
      }
    }).finally(() => { this.flight = undefined; });
    return this.flight;
  }
  async logout(): Promise<void> {
    const previous = this.read();
    const epoch = crypto.randomUUID();
    this.write({epoch, status: 'out', sessionId: previous?.sessionId});
    await this.lock(async () => {
      if (this.read()?.epoch !== epoch) return;
      const result = await this.transport('/auth/refresh/revoke', {});
      if (!result?.data?.confirmed) throw new Error('本机已退出，服务端会话撤销尚未确认');
    });
  }
  invalidate(error: unknown) {
    if (sessionEnded(error)) this.write({epoch:crypto.randomUUID(),status:'out'});
  }
}
